import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';

import { OMNI_THEME } from '@aiz-gl/omni-ui';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { OktaAuthModule } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { routes } from './app.routes';
import { apiKeyInterceptor } from './interceptors/apikey.interceptor';
import { authInterceptor } from './interceptors/auth.interceptor';
import { dccInterceptor } from './interceptors/dcc.interceptor';
import { environmentInterceptor } from './interceptors/environment.interceptor';
import { errorInterceptor } from './interceptors/error.interceptor';
import { messageInterceptor } from './interceptors/message.interceptor';
import { spinnerInterceptors } from './interceptors/spinner-interceptors';
import { tokenInterceptor } from './interceptors/token.interceptor';
import { DarkTheme } from './theme/dark.theme';
import { LightTheme } from './theme/light.theme';

export interface OktaAuthEnv {
  issuer: string;
  clientId: string;
}

// https://developer.okta.com/docs/guides/sign-into-spa-redirect/angular/main/#configure-your-app
const getOktaAuth = (env: OktaAuthEnv) =>
  new OktaAuth({
    ...env,
    redirectUri: window.location.origin + '/login/callback',
    postLogoutRedirectUri: window.location.origin,
    scopes: ['openid', 'profile', 'offline_access', 'email'],
  });

export const appConfig = (oktaAuthEnv: OktaAuthEnv): ApplicationConfig => ({
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    ),
    {
      provide: OMNI_THEME,
      useValue: { light: LightTheme, dark: DarkTheme },
    },
    provideHttpClient(
      withInterceptors([
        apiKeyInterceptor,
        environmentInterceptor,
        spinnerInterceptors,
        messageInterceptor,
        authInterceptor,
        dccInterceptor,
        errorInterceptor,
        tokenInterceptor,
      ]),
    ),
    importProvidersFrom(
      OktaAuthModule.forRoot({ oktaAuth: getOktaAuth(oktaAuthEnv) }),
    ),
  ],
});
